import React, { useState, useEffect } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import SignaturePad from './SignaturePad';
import { signatureService } from '../../../services/signatureService';
import { useAuth } from '../../../contexts/AuthContext';

const ESignatureModal = ({ isOpen, onClose, contract }) => {
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('sign');
  const [signerType, setSignerType] = useState('staff');
  const [signerInfo, setSignerInfo] = useState({
    name: '',
    email: ''
  });
  const [signatures, setSignatures] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSignature, setSelectedSignature] = useState(null);

  useEffect(() => {
    if (isOpen && contract?.id) {
      loadSignatures();
    }
  }, [isOpen, contract]);

  useEffect(() => {
    if (userProfile) {
      setSignerInfo({
        name: userProfile?.full_name || '',
        email: userProfile?.email || user?.email || ''
      });
    }
  }, [userProfile, user]);

  const loadSignatures = async () => {
    try {
      setLoading(true);
      const sigs = await signatureService?.getContractSignatures(contract?.id);
      setSignatures(sigs);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAuditTrail = async (signatureId) => {
    try {
      const trail = await signatureService?.getSignatureAuditTrail(signatureId);
      setAuditTrail(trail);
      setSelectedSignature(signatureId);
    } catch (err) {
      console.error('Error loading audit trail:', err);
    }
  };

  const handleSaveSignature = async (signatureData) => {
    try {
      setLoading(true);
      setError('');

      await signatureService?.createSignature(contract?.id, signatureData, {
        name: signerInfo?.name,
        email: signerInfo?.email,
        type: signerType
      });

      alert('تم حفظ التوقيع بنجاح');
      await loadSignatures();
      setActiveTab('view');
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSignerInfo(prev => ({ ...prev, [field]: value }));
  };

  const getSignerTypeLabel = (type) => {
    const labels = {
      client: 'عميل',
      staff: 'موظف',
      witness: 'شاهد'
    };
    return labels?.[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      signed: '#10B981',
      pending: '#F59E0B',
      rejected: '#EF4444',
      expired: '#6B7280'
    };
    return colors?.[status] || '#6B7280';
  };

  const getStatusLabel = (status) => {
    const labels = {
      signed: 'موقّع',
      pending: 'قيد الانتظار',
      rejected: 'مرفوض',
      expired: 'منتهي'
    };
    return labels?.[status] || status;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="التوقيع الإلكتروني للعقد"
      icon="PenTool"
      iconColor="#06B6D4"
      size="lg"
      footer={null}
    >
      <div className="space-y-6">
        {/* Tabs */}
        <div className="flex gap-2 border-b border-border">
          <button
            onClick={() => setActiveTab('sign')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'sign' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            التوقيع
          </button>
          <button
            onClick={() => setActiveTab('view')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'view' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            التوقيعات ({signatures?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'audit' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            سجل التدقيق
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={16} color="#EF4444" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Sign Tab */}
        {activeTab === 'sign' && (
          <div className="space-y-4">
            {/* Contract Info */}
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-foreground mb-2">معلومات العقد</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">رقم العقد:</span>
                  <span className="text-foreground font-medium mr-2">
                    {contract?.contract_number || 'غير محدد'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">نوع العقد:</span>
                  <span className="text-foreground font-medium mr-2">
                    {contract?.contract_type || 'غير محدد'}
                  </span>
                </div>
              </div>
            </div>

            {/* Signer Type Selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                نوع الموقّع
              </label>
              <div className="grid grid-cols-3 gap-3">
                {['staff', 'client', 'witness']?.map((type) => (
                  <button
                    key={type}
                    onClick={() => setSignerType(type)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      signerType === type
                        ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <span className="text-sm font-medium text-foreground">
                      {getSignerTypeLabel(type)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Signer Information */}
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="اسم الموقّع"
                value={signerInfo?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                placeholder="الاسم الكامل"
                required
              />
              <Input
                label="البريد الإلكتروني"
                type="email"
                value={signerInfo?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                placeholder="email@example.com"
                required
              />
            </div>

            {/* Signature Pad */}
            <SignaturePad
              onSave={handleSaveSignature}
              onCancel={onClose}
              signerName={signerInfo?.name}
            />
          </div>
        )}

        {/* View Signatures Tab */}
        {activeTab === 'view' && (
          <div className="space-y-3">
            {loading ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">جاري التحميل...</p>
              </div>
            ) : signatures?.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="FileSignature" size={48} color="#9CA3AF" />
                <p className="text-muted-foreground mt-2">لا توجد توقيعات بعد</p>
              </div>
            ) : (
              signatures?.map((sig) => (
                <div
                  key={sig?.id}
                  className="p-4 rounded-lg border border-border hover:border-primary transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="User" size={16} color="#DAA520" />
                        <h4 className="text-sm font-semibold text-foreground">
                          {sig?.signerName}
                        </h4>
                        <span
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: `${getStatusColor(sig?.status)}20`,
                            color: getStatusColor(sig?.status)
                          }}
                        >
                          {getStatusLabel(sig?.status)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                        <div>
                          <span>النوع:</span>
                          <span className="text-foreground mr-1">
                            {getSignerTypeLabel(sig?.signerType)}
                          </span>
                        </div>
                        <div>
                          <span>البريد:</span>
                          <span className="text-foreground mr-1">{sig?.signerEmail}</span>
                        </div>
                        <div>
                          <span>التاريخ:</span>
                          <span className="text-foreground mr-1">
                            {new Date(sig?.signedAt)?.toLocaleDateString('ar-SA')}
                          </span>
                        </div>
                        <div>
                          <span>IP:</span>
                          <span className="text-foreground mr-1">{sig?.ipAddress}</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        loadAuditTrail(sig?.id);
                        setActiveTab('audit');
                      }}
                    >
                      <Icon name="FileText" size={16} />
                      <span className="mr-2">السجل</span>
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Audit Trail Tab */}
        {activeTab === 'audit' && (
          <div className="space-y-3">
            {!selectedSignature ? (
              <div className="text-center py-8">
                <Icon name="FileSearch" size={48} color="#9CA3AF" />
                <p className="text-muted-foreground mt-2">
                  اختر توقيعاً من قائمة التوقيعات لعرض سجل التدقيق
                </p>
              </div>
            ) : auditTrail?.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">لا توجد سجلات تدقيق</p>
              </div>
            ) : (
              <div className="space-y-2">
                {auditTrail?.map((entry) => (
                  <div
                    key={entry?.id}
                    className="p-3 rounded-lg bg-muted/30 border border-border"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-primary mt-1.5" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-foreground">
                            {entry?.action}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(entry?.createdAt)?.toLocaleString('ar-SA')}
                          </span>
                        </div>
                        {entry?.metadata && (
                          <div className="text-xs text-muted-foreground">
                            <pre className="whitespace-pre-wrap">
                              {JSON.stringify(entry?.metadata, null, 2)}
                            </pre>
                          </div>
                        )}
                        {entry?.ipAddress && (
                          <p className="text-xs text-muted-foreground mt-1">
                            IP: {entry?.ipAddress}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ESignatureModal;